import { useSyncExternalStore } from 'react'
import { getCapacitorPlatform, isNativePlatform } from '@/lib/nativePlatform'

export type PlatformKind =
  | 'web'
  | 'ios' // iPhone
  | 'ipados' // iPad (and "Designed for iPad" on Mac / Vision Pro)
  | 'android'

interface PlatformInfo {
  kind: PlatformKind
  /** True for any Apple platform reached through Capacitor */
  isApple: boolean
  /** True when running inside any Capacitor WebView */
  isNative: boolean
  /** True when billing CTAs must be hidden (Apple + native Android) */
  billingDisabled: boolean
  /** Mac Catalyst — "Designed for iPad" running on macOS via the
   *  iPadOS binary. UA contains "Macintosh" + touch support.
   *  Distinguished from actual iPad because Catalyst's embedded
   *  web view tends to behave more like desktop Safari for
   *  AudioWorklet / WASM purposes, whereas real iPad WKWebView
   *  has stricter limits. */
  isCatalyst: boolean
  /** Actual iPad hardware (or simulator). Separate from Catalyst
   *  because the DSP stack behaves differently. */
  isIPad: boolean
}

function detect(): PlatformInfo {
  const native = isNativePlatform()
  const base = getCapacitorPlatform()

  if (!native) {
    return {
      kind: 'web',
      isApple: false,
      isNative: false,
      billingDisabled: false,
      isCatalyst: false,
      isIPad: false,
    }
  }

  if (base === 'android') {
    return {
      kind: 'android',
      isApple: false,
      isNative: true,
      // Google Play also requires a billing library for digital goods.
      // Out of scope for this PR — gate here too.
      billingDisabled: true,
      isCatalyst: false,
      isIPad: false,
    }
  }

  // Capacitor reports `'ios'` for iPhone, iPad, "Designed for iPad" on
  // Apple Silicon Mac, and "Designed for iPad" on Vision Pro — the same
  // binary runs in all four environments. We split iPhone from iPad via
  // UA because our layout treats iPad landscape as the v1 quality bar
  // and iPhone as functional-but-not-pretty. Mac / Vision Pro both
  // report as iPad-class here, which is the right behavior: they
  // render the iPad UI unchanged.
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  const maxTouchPoints =
    typeof navigator !== 'undefined' ? navigator.maxTouchPoints : 0
  // iPadOS 13+ reports itself as "Macintosh; Intel Mac OS X" with
  // touch support. "Designed for iPad" on Mac/Vision Pro also lands
  // here. We treat all three as 'ipados'.
  const isRealIPad = /iPad/i.test(ua)
  const isCatalyst = /Macintosh/i.test(ua) && maxTouchPoints > 1
  const isIPadClass = isRealIPad || isCatalyst

  return {
    kind: isIPadClass ? 'ipados' : 'ios',
    isApple: true,
    isNative: true,
    billingDisabled: true,
    isCatalyst,
    isIPad: isRealIPad,
  }
}

// usePlatform returns a stable snapshot. The platform never changes
// during the lifetime of a render, so we use useSyncExternalStore with
// a no-op subscribe — this is just a convenient way to get a stable
// cached value without useMemo gymnastics.
const subscribe = () => () => {}
let cached: PlatformInfo | null = null
function getSnapshot(): PlatformInfo {
  if (!cached) cached = detect()
  return cached
}

export function usePlatform(): PlatformInfo {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
